<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">Order Management</h1>
        <p class="mt-2 text-gray-600">View and update order status</p>
      </div>

      <!-- Filters -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select v-model="selectedStatus" class="w-full border border-gray-300 rounded-md px-3 py-2">
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="advance_paid">Advance Paid</option>
              <option value="in_workshop">In Workshop</option>
              <option value="completed">Completed</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <input type="date" v-model="startDate" class="w-full border border-gray-300 rounded-md px-3 py-2">
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">To</label>
            <input type="date" v-model="endDate" class="w-full border border-gray-300 rounded-md px-3 py-2">
          </div>
          <div class="flex items-end">
            <button @click="applyFilters" class="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700">
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading orders...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <div class="text-red-500 mb-4">
          <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">Error Loading Orders</h3>
        <p class="text-gray-500 mb-4">{{ error }}</p>
        <button @click="loadOrders" class="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700">
          Try Again
        </button>
      </div>

      <!-- Orders Table -->
      <div v-else class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Weight</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Weight</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="order in filteredOrders" :key="order.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  #{{ order.order_no }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ order.customer_name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ order.item_count }} items
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ getTotalGrossWeight(order.items) }}g
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ getTotalNetWeight(order.items) }}g
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getStatusClass(order.status, order)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ formatStatus(order.status, order) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(order.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button @click="updateStatus(order.id)" class="text-indigo-600 hover:text-indigo-900 mr-3">
                    Update Status
                  </button>
                  <button @click="viewDetails(order.id)" class="text-gray-600 hover:text-gray-900">
                    View Details
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && !error && filteredOrders.length === 0" class="text-center py-12">
        <div class="text-gray-400 mb-4">
          <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
        <p class="text-gray-500">Try adjusting your filters or check back later.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { ordersAPI } from '../../services/api'

export default {
  name: 'StaffOrders',
  setup() {
    const selectedStatus = ref('')
    const startDate = ref('')
    const endDate = ref('')
    const loading = ref(false)
    const error = ref(null)

    // Real orders data from API
    const orders = ref([])

    const filteredOrders = computed(() => {
      let filtered = orders.value

      if (selectedStatus.value) {
        filtered = filtered.filter(order => order.status === selectedStatus.value)
      }

      if (startDate.value) {
        filtered = filtered.filter(order => new Date(order.created_at) >= new Date(startDate.value))
      }

      if (endDate.value) {
        filtered = filtered.filter(order => new Date(order.created_at) <= new Date(endDate.value))
      }

      return filtered
    })

    const formatPrice = (price) => {
      return price.toLocaleString('en-IN')
    }

    const formatStatus = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, show "Pending in Workshop"
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'Pending in Workshop'
      }
      
      const statusMap = {
        pending: 'Pending',
        advance_paid: 'Advance Paid',
        in_workshop: 'In Workshop',
        completed: 'Completed',
        delivered: 'Delivered'
      }
      return statusMap[status] || status
    }

    const getStatusClass = (status, order) => {
      // Special case: if status is pending but order is assigned to workshop, use workshop styling
      if (status === 'pending' && order && order.assigned_workshop_user) {
        return 'bg-purple-100 text-purple-800'
      }
      
      const classMap = {
        pending: 'bg-yellow-100 text-yellow-800',
        advance_paid: 'bg-blue-100 text-blue-800',
        in_workshop: 'bg-purple-100 text-purple-800',
        completed: 'bg-green-100 text-green-800',
        delivered: 'bg-gray-100 text-gray-800'
      }
      return classMap[status] || 'bg-gray-100 text-gray-800'
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-IN')
    }

    const getTotalGrossWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const grossWeight = parseFloat(item.gross_weight) || 0
        return total + (grossWeight * quantity)
      }, 0).toFixed(2)
    }

    const getTotalNetWeight = (items) => {
      if (!items || !Array.isArray(items)) return 0
      return items.reduce((total, item) => {
        const quantity = item.quantity || item.qty || 0
        const netWeight = parseFloat(item.net_weight) || 0
        return total + (netWeight * quantity)
      }, 0).toFixed(2)
    }

    const loadOrders = async () => {
      loading.value = true
      error.value = null
      try {
        const response = await ordersAPI.getOrders()
        orders.value = response.data.orders || []
      } catch (err) {
        console.error('Error loading orders:', err)
        error.value = 'Failed to load orders. Please try again.'
        orders.value = []
      } finally {
        loading.value = false
      }
    }

    const applyFilters = async () => {
      // Reload orders with filters
      await loadOrders()
    }

    const updateStatus = (orderId) => {
      // In real app, this would open a modal to update status
      console.log('Update status for order:', orderId)
    }

    const viewDetails = (orderId) => {
      // In real app, this would navigate to order details
      console.log('View details for order:', orderId)
    }

    onMounted(() => {
      loadOrders()
    })

    return {
      selectedStatus,
      startDate,
      endDate,
      loading,
      error,
      orders,
      filteredOrders,
      formatPrice,
      formatStatus,
      getStatusClass,
      formatDate,
      getTotalGrossWeight,
      getTotalNetWeight,
      applyFilters,
      updateStatus,
      viewDetails
    }
  }
}
</script> 